import { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { ShieldStar, Users, Package, Receipt, Scales, Gavel, ChartLine, SignOut, ShieldCheck } from '@phosphor-icons/react';
import { API } from '@/App';
import DropioLogo from '@/components/DropioLogo';

const nav = [
  { to: '/admin', end: true, icon: ChartLine, label: 'Overview' },
  { to: '/admin/trust-safety', icon: ShieldCheck, label: 'Trust & Safety' },
  { to: '/admin/rules', icon: ShieldStar, label: 'Rules Editor' },
  { to: '/admin/disputes', icon: Gavel, label: 'Disputes' },
];

const AdminShell = () => {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/auth/me`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((u) => {
        if (!u || !u.is_admin) {
          navigate('/login', { replace: true });
          return;
        }
        setAdmin(u);
        setLoading(false);
      })
      .catch(() => navigate('/login', { replace: true }));
  }, [navigate]);

  const handleLogout = async () => {
    await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center text-gray-500" data-testid="admin-loading">
        Loading admin…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="admin-shell">
      <aside className="fixed inset-y-0 left-0 w-64 bg-white border-r border-gray-200 z-20 hidden md:flex flex-col">
        <div className="p-6 border-b border-gray-100">
          <DropioLogo size={32} />
          <p className="text-[10px] font-bold uppercase tracking-widest text-[#6D28D9] mt-3">Admin Console</p>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {nav.map(({ to, end, icon: Icon, label }) => (
            <NavLink key={to} to={to} end={end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition ${
                  isActive ? 'dropio-gradient text-white' : 'text-gray-600 hover:bg-purple-50 hover:text-[#6D28D9]'
                }`
              }
              data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}>
              <Icon size={18} weight="bold" /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-gray-100">
          <p className="text-xs font-bold text-gray-900 truncate" data-testid="admin-name">{admin?.name}</p>
          <p className="text-[11px] text-gray-500 truncate mb-3">{admin?.email}</p>
          <button onClick={handleLogout} className="w-full text-left flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-[#6D28D9]" data-testid="admin-logout">
            <SignOut size={16} weight="bold" /> Logout
          </button>
        </div>
      </aside>
      <main className="md:ml-64 p-6 md:p-10">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminShell;
